import React, { useEffect, useState } from "react";
import { api, exportCsv } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Check } from "@/components/Check";
import { Plus, Download, Trash2, Send, AlertCircle, Lock, Upload } from "lucide-react";
import useResizableColumns from "@/lib/useResizableColumns";
import CalendarTransfer from "@/components/CalendarTransfer";

const TYPES = ["Showing", "Inspection", "Closing", "Meeting", "Shoot", "Personal", "Other"];

const fmtTransferDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function CalendarSync() {
  const { user } = useAuth();
  const isPro = !!user && user.plan !== "free";
  const [events, setEvents] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);
  const [transfer, setTransfer] = useState(null); // "import" | "export" | null
  const [googleReady, setGoogleReady] = useState(false);
  const [loadVersion, setLoadVersion] = useState(0);
  const { setRef, widthStyle, Grip, reset } = useResizableColumns(
    "calendar-v2", 10, { minWidth: 50 },
  );

  const load = async () => {
    if (!isPro) return;
    const e = await api.get("/calendar-events").then((r) => r.data);
    setEvents(Array.isArray(e) ? e : []);
    setLoadVersion((v) => v + 1);
  };

  useEffect(() => {
    load();
    api.get("/auth/google/status").then((r) => setGoogleReady(!!r.data?.has_calendar_access)).catch(() => {});
  }, []);

  const add = async () => {
    const e = await api
      .post("/calendar-events", { sync: false, title: "" })
      .then((r) => r.data);
    setEvents((p) => [e, ...p]);
  };

  const update = async (id, patch) => {
    const cur = events.find((e) => e.id === id);
    if (!cur) return;
    const next = { ...cur, ...patch };
    setEvents((p) => p.map((e) => (e.id === id ? next : e)));
    await api.put(`/calendar-events/${id}`, next);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    setEvents((p) => p.filter((e) => e.id !== id));
    await api.delete(`/calendar-events/${id}`);
  };

  const pushChecked = async () => {
    const candidates = events.filter(
      (e) => e.sync && e.title && e.date && !e.google_event_id
    );
    if (candidates.length === 0) {
      setPushMsg({ type: "info", text: "Nothing checked to push." });
      setTimeout(() => setPushMsg(null), 3500);
      return;
    }
    setPushing(true);
    let okCount = 0;
    let failed = null;
    for (const ev of candidates) {
      try {
        const r = await api.post(`/calendar/push/event/${ev.id}`);
        if (r.data?.google_event_id) {
          setEvents((p) =>
            p.map((e) =>
              e.id === ev.id
                ? {
                    ...e,
                    google_event_id: r.data.google_event_id,
                    google_event_link: r.data.google_event_link,
                  }
                : e
            )
          );
          okCount += 1;
        }
      } catch (e) {
        failed = e.response?.data?.detail || e.message;
        break;
      }
    }
    setPushing(false);
    if (failed) {
      setPushMsg({
        type: "warn",
        text: failed.includes("Connect Google")
          ? "Connect your Google account in Settings first to enable Calendar sync."
          : `Push stopped: ${failed}`,
      });
    } else {
      setPushMsg({
        type: "info",
        text: `Pushed ${okCount} event${okCount === 1 ? "" : "s"} to Google Calendar.`,
      });
    }
    setTimeout(() => setPushMsg(null), 6000);
  };

  const onExport = () => exportCsv("calendar-events.csv", events);

  if (!isPro) {
    return (
      <div className="space-y-6" data-testid="calendar-sync">
        <header>
          <div className="kpi-label">Calendar</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            Mission window<span className="text-hud">_</span>uplink.
          </h1>
        </header>
        <div className="hud-frame p-10 text-center space-y-4" data-testid="calendar-locked">
          <Lock size={20} className="mx-auto text-oud" />
          <div className="display text-xl uppercase">Google Calendar sync is a Pro feature</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Push deals, shoots and events straight to your Google Calendar, and keep your
            operations calendar in one place.
          </p>
          <Link to="/billing" className="hud-btn hud-btn-primary inline-flex" data-testid="calendar-upgrade">
            UPGRADE TO PRO — $10/MO
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-sync">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">Calendar</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            Mission window<span className="text-hud">_</span>uplink.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={add} className="hud-btn flex items-center gap-2" data-testid="event-add">
            <Plus size={12} /> NEW EVENT
          </button>
          <button
            onClick={() => setTransfer("import")}
            className="hud-btn flex items-center gap-2"
            data-testid="calendar-import"
          >
            <Upload size={12} /> IMPORT
          </button>
          <button
            onClick={() => setTransfer("export")}
            className="hud-btn flex items-center gap-2"
            data-testid="calendar-export-transfer"
          >
            <Download size={12} /> EXPORT
          </button>
          <button
            onClick={pushChecked}
            disabled={pushing}
            className="hud-btn hud-btn-primary flex items-center gap-2"
            data-testid="push-google"
          >
            <Send size={12} /> PUSH CHECKED → GOOGLE
          </button>
          <button onClick={onExport} className="hud-btn flex items-center gap-2" data-testid="event-export">
            <Download size={12} /> CSV
          </button>
          <button onClick={reset} className="hud-btn !text-[10px]" title="Reset column widths" data-testid="event-reset-widths">
            RESET WIDTHS
          </button>
        </div>
      </header>

      <CalendarTransfer
        open={!!transfer}
        mode={transfer}
        googleReady={googleReady}
        onClose={() => setTransfer(null)}
        onChanged={load}
      />

      {pushMsg && (
        <div
          className="hud-frame p-4 flex items-start gap-3"
          style={{ borderColor: "hsl(var(--oxblood) / 0.6)" }}
          data-testid="push-msg"
        >
          <AlertCircle size={14} className="text-[hsl(var(--oxblood))] mt-0.5" />
          <div className="text-sm leading-relaxed">{pushMsg.text}</div>
        </div>
      )}

      <div className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                <th ref={setRef(0)} style={widthStyle(0, "70px")} className="!text-center">SYNC<Grip index={0} /></th>
                <th ref={setRef(1)} style={widthStyle(1, "240px")}>TITLE<Grip index={1} /></th>
                <th ref={setRef(2)} style={widthStyle(2, "130px")}>DATE<Grip index={2} /></th>
                <th ref={setRef(3)} style={widthStyle(3, "100px")}>START<Grip index={3} /></th>
                <th ref={setRef(4)} style={widthStyle(4, "100px")}>END<Grip index={4} /></th>
                <th ref={setRef(5)} style={widthStyle(5, "130px")}>TYPE<Grip index={5} /></th>
                <th ref={setRef(6)} style={widthStyle(6, "200px")}>LOCATION<Grip index={6} /></th>
                <th ref={setRef(7)} style={widthStyle(7, "240px")}>NOTES<Grip index={7} /></th>
                <th ref={setRef(8)} style={widthStyle(8, "180px")}>TRANSFER<Grip index={8} /></th>
                <th ref={setRef(9)} style={widthStyle(9, "48px")} />
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={`${e.id}-${loadVersion}`} data-testid={`event-row-${e.id}`}>
                  <td className="!text-center">
                    <Check
                      checked={e.sync}
                      onChange={(v) => update(e.id, { sync: v })}
                      testid={`event-sync-${e.id}`}
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <input
                        defaultValue={e.title}
                        onBlur={(ev) =>
                          ev.target.value !== e.title &&
                          update(e.id, { title: ev.target.value })
                        }
                        className="hud-input !border-transparent hover:!border-border !py-1 flex-1"
                        placeholder="Title"
                      />
                      {e.recurrence && (
                        <span
                          title={`Recurring${e.series === "occurrence" ? " (single occurrence)" : " series"}`}
                          className="mono text-[9px] uppercase text-[hsl(var(--oxblood))] border border-[hsl(var(--oxblood))] rounded-full px-1.5 py-0.5 shrink-0"
                          data-testid={`event-recur-${e.id}`}
                        >↻ {e.series === "occurrence" ? "1x" : "SERIES"}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <input
                      type="date"
                      defaultValue={e.date || ""}
                      onBlur={(ev) => update(e.id, { date: ev.target.value || null })}
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      defaultValue={e.start_time || ""}
                      onBlur={(ev) =>
                        update(e.id, { start_time: ev.target.value || null })
                      }
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      defaultValue={e.end_time || ""}
                      onBlur={(ev) =>
                        update(e.id, { end_time: ev.target.value || null })
                      }
                      className="hud-input !py-1"
                    />
                  </td>
                  <td>
                    <select
                      value={e.type || ""}
                      onChange={(ev) => update(e.id, { type: ev.target.value })}
                      className="hud-input !py-1 !bg-transparent"
                    >
                      <option value="">—</option>
                      {TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      defaultValue={e.location || ""}
                      onBlur={(ev) =>
                        ev.target.value !== (e.location || "") && update(e.id, { location: ev.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1 min-w-[140px]"
                      placeholder="Location"
                      data-testid={`event-location-${e.id}`}
                    />
                  </td>
                  <td>
                    <input
                      defaultValue={e.notes}
                      onBlur={(ev) =>
                        ev.target.value !== e.notes && update(e.id, { notes: ev.target.value })
                      }
                      className="hud-input !border-transparent hover:!border-border !py-1 min-w-[160px]"
                      placeholder="Notes"
                    />
                  </td>
                  <td>
                    {e.last_transfer ? (
                      <div className="flex flex-col leading-tight" data-testid={`event-transfer-${e.id}`}>
                        <span className="mono text-[10px] uppercase tracking-wide text-[hsl(var(--oxblood))]">
                          {e.last_transfer.direction === "export" ? "↑ Exported" : "↓ Imported"}
                          {" · "}
                          {e.last_transfer.provider === "google" ? "Google" : ".ics"}
                        </span>
                        <span className="mono text-[9px] text-muted-foreground">
                          {fmtTransferDate(e.last_transfer.at)}
                        </span>
                      </div>
                    ) : e.google_event_id ? (
                      e.google_event_link ? (
                        <a href={e.google_event_link} target="_blank" rel="noreferrer"
                          className="mono text-[10px] text-muted-foreground hover:text-[hsl(var(--oxblood))]"
                          data-testid={`event-transfer-${e.id}`}>Linked · Google</a>
                      ) : (
                        <span className="mono text-[10px] text-muted-foreground" data-testid={`event-transfer-${e.id}`}>Linked · Google</span>
                      )
                    ) : (
                      <span className="mono text-[10px] text-muted-foreground" data-testid={`event-transfer-${e.id}`}>—</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => remove(e.id)}
                      className="text-muted-foreground hover:text-[hsl(var(--signal))]"
                      data-testid={`event-delete-${e.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted-foreground py-10">
                    No events yet. Click <span className="text-[hsl(var(--oxblood))]">NEW EVENT</span> to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
